/**
 * Application Configuration
 * 
 * Root configuration for the Angular application with all providers.
 * 
 * @remarks
 * - Includes global error handler
 * - Configures HTTP client with interceptors
 * - Sets up routing with preloading strategy
 * - Enables animations
 * - Configures API base URL from environment
 */

import { ApplicationConfig, ErrorHandler } from '@angular/core';
import { provideRouter, withPreloading, PreloadAllModules, withComponentInputBinding, withViewTransitions, withInMemoryScrolling, TitleStrategy } from '@angular/router';
import { PageTitleStrategy } from './core/services/page-title.strategy';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { routes } from './app.routes';
import { authInterceptor } from './core/interceptors';
import { demoInterceptor } from './core/demo/demo.interceptor';
import { GlobalErrorHandler } from './core/services/global-error-handler.service';
import { ApiConfiguration } from './api/api-configuration';
import { environment } from '../environments/environment';

export const appConfig: ApplicationConfig = {
  providers: [
    // Global error handler
    {
      provide: ErrorHandler,
      useClass: GlobalErrorHandler
    },
    { provide: TitleStrategy, useClass: PageTitleStrategy },

    // API Configuration - set root URL from environment
    {
      provide: ApiConfiguration,
      useFactory: () => {
        const config = new ApiConfiguration();
        config.rootUrl = environment.apiUrl;
        return config;
      }
    },
    
    // Router with preloading strategy and modern features
    provideRouter(
      routes,
      withPreloading(PreloadAllModules),
      withComponentInputBinding(), // Bind route params to component inputs
      withViewTransitions(), // Enable view transitions API
      withInMemoryScrolling({ scrollPositionRestoration: 'top', anchorScrolling: 'enabled' })
    ),
    
    // HTTP client with interceptors
    // demoInterceptor runs first so it can short-circuit before authInterceptor's
    // token/refresh logic ever executes, when environment.demo is enabled.
    provideHttpClient(
      withInterceptors([
        ...(environment.demo ? [demoInterceptor] : []),
        authInterceptor
      ])
    ),
    
    // Animations
    provideAnimations()
  ]
};
